import { useServices, useToast } from '@/app/providers';
import { Button, Card } from '@/core/ui/components';
import type {
  GuestRegistrationMode,
  GuestValidityMode,
  HikCentralOrganization,
  HikCentralSuperConfig,
} from '@yawi/types';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  Save,
  Settings2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface HikCentralSuperConfigTabProps {
  tenantId: string;
  integrationId: string;
}

type ReconcilePhase = 'idle' | 'running' | 'completed' | 'failed';

export function HikCentralSuperConfigTab({ tenantId, integrationId }: HikCentralSuperConfigTabProps) {
  const { hikCentralSuperConfigRepository } = useServices();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [config, setConfig] = useState<HikCentralSuperConfig | null>(null);
  const [draft, setDraft] = useState<HikCentralSuperConfig | null>(null);

  const [orgs, setOrgs] = useState<HikCentralOrganization[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState<string | null>(null);

  const [reconcilePhase, setReconcilePhase] = useState<ReconcilePhase>('idle');
  const [reconcileQueued, setReconcileQueued] = useState<number | null>(null);
  const [reconcileProgress, setReconcileProgress] = useState<{ completed: number; total: number; failed: number } | null>(null);
  const [reconcileStarting, setReconcileStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await hikCentralSuperConfigRepository.getSuperConfig(integrationId, tenantId);
      setConfig(cfg);
      setDraft(cfg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar la configuración avanzada');
    } finally {
      setLoading(false);
    }
  }, [hikCentralSuperConfigRepository, integrationId, tenantId]);

  const loadOrgs = useCallback(async () => {
    setOrgsLoading(true);
    setOrgsError(null);
    try {
      const list = await hikCentralSuperConfigRepository.getOrganizations(integrationId, tenantId);
      setOrgs(list);
    } catch (e) {
      setOrgsError(e instanceof Error ? e.message : 'Error al cargar organizaciones');
    } finally {
      setOrgsLoading(false);
    }
  }, [hikCentralSuperConfigRepository, integrationId, tenantId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = await hikCentralSuperConfigRepository.getSuperConfig(integrationId, tenantId);
        if (cancelled) return;
        setConfig(cfg);
        setDraft(cfg);
        if (cfg.guestRegistrationMode === 'PERSON') {
          loadOrgs();
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Error al cargar la configuración avanzada');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [hikCentralSuperConfigRepository, integrationId, tenantId, loadOrgs]);

  // Poll reconcile status while running
  useEffect(() => {
    if (reconcilePhase !== 'running') return;
    const poll = async () => {
      try {
        const status = await hikCentralSuperConfigRepository.getReconcileStatus(tenantId);
        setReconcileProgress({ completed: status.completed, total: status.total, failed: status.failed });
        if (status.status === 'running') {
          pollRef.current = setTimeout(poll, 3000);
        } else if (status.status === 'completed') {
          setReconcilePhase('completed');
          if (status.failed > 0) {
            showToast('warning', `${status.completed}/${status.total} residentes movidos. ${status.failed} fallaron — usar Full Sync para reintentar.`);
          } else {
            showToast('success', `${status.completed} residentes movidos correctamente.`);
          }
        } else if (status.status === 'failed') {
          setReconcilePhase('failed');
          showToast('error', 'La reconciliación falló. Revisar logs y reintentar.');
        }
      } catch {
        pollRef.current = setTimeout(poll, 5000);
      }
    };
    pollRef.current = setTimeout(poll, 2000);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [reconcilePhase, hikCentralSuperConfigRepository, tenantId, showToast]);

  const dirty =
    draft !== null &&
    config !== null &&
    (draft.guestRegistrationMode !== config.guestRegistrationMode ||
      draft.guestValidityMode !== config.guestValidityMode ||
      draft.residentOrgIndexCode !== config.residentOrgIndexCode ||
      draft.guestOrgIndexCode !== config.guestOrgIndexCode ||
      draft.storeRawWebhookPayload !== config.storeRawWebhookPayload ||
      draft.snapshotOnAccessEnabled !== config.snapshotOnAccessEnabled);

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const updated = await hikCentralSuperConfigRepository.updateSuperConfig(integrationId, {
        guestRegistrationMode: draft.guestRegistrationMode,
        guestValidityMode: draft.guestValidityMode,
        storeRawWebhookPayload: draft.storeRawWebhookPayload,
        snapshotOnAccessEnabled: draft.snapshotOnAccessEnabled,
        ...(draft.guestRegistrationMode === 'PERSON' && {
          residentOrgIndexCode: draft.residentOrgIndexCode ?? undefined,
          guestOrgIndexCode: draft.guestOrgIndexCode ?? undefined,
        }),
      }, tenantId);
      setConfig(updated);
      setDraft(updated);
      showToast('success', 'Configuración avanzada actualizada.');
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReconcile = async () => {
    if (!config?.residentOrgIndexCode) return;
    setReconcileStarting(true);
    try {
      const result = await hikCentralSuperConfigRepository.reconcileOrg(
        { targetOrgIndexCode: config.residentOrgIndexCode },
        tenantId,
      );
      setReconcileQueued(result.queued);
      if (result.queued === 0) {
        showToast('info', 'No hay residentes sincronizados para mover.');
      } else {
        setReconcilePhase('running');
        setReconcileProgress(null);
      }
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Error al iniciar reconciliación');
    } finally {
      setReconcileStarting(false);
    }
  };

  const setDraftField = <K extends keyof HikCentralSuperConfig>(key: K, value: HikCentralSuperConfig[K]) => {
    setDraft((prev) => prev ? { ...prev, [key]: value } : prev);
  };

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      </Card>
    );
  }

  if (!draft) return null;

  const isPersonMode = draft.guestRegistrationMode === 'PERSON';

  return (
    <div className="space-y-4">
      {/* Configuración principal */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="w-4 h-4 text-indigo-500" />
          <h3 className="text-base font-semibold text-theme">Configuración Avanzada HikCentral</h3>
        </div>

        {/* Modo de registro de guests */}
        <div className="mb-5">
          <p className="text-sm font-medium text-theme mb-2">Modo de registro de guests</p>
          <div className="flex gap-3">
            {(['VISITOR', 'PERSON'] as GuestRegistrationMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => {
                  setDraftField('guestRegistrationMode', mode);
                  if (mode === 'PERSON' && orgs.length === 0) loadOrgs();
                }}
                className={`flex-1 border rounded-lg px-4 py-3 text-sm text-left transition-colors ${
                  draft.guestRegistrationMode === mode
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                    : 'border-theme-border bg-theme-bg text-theme hover:border-indigo-300'
                }`}
              >
                <p className="font-semibold">{mode}</p>
                <p className="text-xs mt-0.5 opacity-70">
                  {mode === 'VISITOR'
                    ? 'Visitor Management API — requiere módulo Visitor en HikCentral'
                    : 'Personas regulares, separados por organización HikCentral'}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Modo de validez */}
        <div className="mb-5">
          <p className="text-sm font-medium text-theme mb-2">Modo de validez de fechas</p>
          <div className="flex gap-3">
            {(['EXACT', 'FULL_DAY'] as GuestValidityMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setDraftField('guestValidityMode', mode)}
                className={`flex-1 border rounded-lg px-4 py-3 text-sm text-left transition-colors ${
                  draft.guestValidityMode === mode
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                    : 'border-theme-border bg-theme-bg text-theme hover:border-indigo-300'
                }`}
              >
                <p className="font-semibold">{mode}</p>
                <p className="text-xs mt-0.5 opacity-70">
                  {mode === 'EXACT'
                    ? 'Usa las horas exactas ingresadas por el residente'
                    : 'Expande a 00:00:00–23:59:59 en el timezone del tenant'}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Guardar payload completo de webhooks */}
        <div className="mb-5">
          <div className="flex items-center justify-between">
            <div className="flex-1 pr-4">
              <label
                htmlFor="storeRawWebhookPayload"
                className="text-sm font-medium text-theme cursor-pointer"
              >
                Guardar payload completo de webhooks
              </label>
              <p
                id="storeRawWebhookPayload-desc"
                className="text-xs text-theme-secondary mt-0.5"
              >
                Habilita el almacenamiento del payload JSON completo de cada evento entrante de HikCentral.
                Útil para depurar eventos denegados o códigos desconocidos.
              </p>
            </div>
            <button
              id="storeRawWebhookPayload"
              type="button"
              role="switch"
              aria-checked={draft.storeRawWebhookPayload}
              aria-describedby="storeRawWebhookPayload-desc"
              onClick={() => setDraftField('storeRawWebhookPayload', !draft.storeRawWebhookPayload)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                draft.storeRawWebhookPayload ? 'bg-indigo-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  draft.storeRawWebhookPayload ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {draft.storeRawWebhookPayload && (
            <div
              role="alert"
              className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Activar esta opción incrementará el tamaño de la base de datos rápidamente.
                Recomendado solo durante sesiones de depuración. Recuerda desactivarlo al finalizar.
              </span>
            </div>
          )}
        </div>

        {/* Adjuntar foto a eventos de acceso */}
        <div className="mb-5">
          <div className="flex items-center justify-between">
            <div className="flex-1 pr-4">
              <label
                htmlFor="snapshotOnAccessEnabled"
                className="text-sm font-medium text-theme cursor-pointer"
              >
                Adjuntar foto a eventos de acceso
              </label>
              <p
                id="snapshotOnAccessEnabled-desc"
                className="text-xs text-theme-secondary mt-0.5"
              >
                Captura un snapshot de la cámara mapeada a la puerta en eventos de acceso concedido o denegado.
                Requiere que cada puerta tenga cámara configurada en la pestaña "Puertas y Cámaras".
              </p>
            </div>
            <button
              id="snapshotOnAccessEnabled"
              type="button"
              role="switch"
              aria-checked={draft.snapshotOnAccessEnabled}
              aria-describedby="snapshotOnAccessEnabled-desc"
              onClick={() => setDraftField('snapshotOnAccessEnabled', !draft.snapshotOnAccessEnabled)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                draft.snapshotOnAccessEnabled ? 'bg-indigo-500' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  draft.snapshotOnAccessEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {draft.snapshotOnAccessEnabled && (
            <div
              role="alert"
              className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Verifica que las puertas tengan cámara asignada en la pestaña "Puertas y Cámaras".
                Las puertas sin cámara no generarán foto. La URL del snapshot expira a los 15 minutos.
              </span>
            </div>
          )}
        </div>

        {/* Organizaciones (solo en modo PERSON) */}
        {isPersonMode && (
          <div className="mb-5 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-theme">Separación por Organización HikCentral</p>
              <button
                type="button"
                onClick={loadOrgs}
                disabled={orgsLoading}
                className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 disabled:opacity-50"
              >
                <RefreshCcw className={`w-3 h-3 ${orgsLoading ? 'animate-spin' : ''}`} />
                Recargar
              </button>
            </div>

            {orgsError && (
              <div className="flex items-start gap-2 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{orgsError}</span>
              </div>
            )}

            <OrgSelect
              label="Organización de Residentes"
              value={draft.residentOrgIndexCode}
              orgs={orgs}
              loading={orgsLoading}
              onChange={(v) => setDraftField('residentOrgIndexCode', v)}
            />
            <OrgSelect
              label="Organización de Visitantes / Guests"
              value={draft.guestOrgIndexCode}
              orgs={orgs}
              loading={orgsLoading}
              onChange={(v) => setDraftField('guestOrgIndexCode', v)}
            />
            <p className="text-xs text-theme-secondary">
              Las dos organizaciones deben ser diferentes. Usar los valores <code className="font-mono">orgIndexCode</code> de HikCentral.
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Save className="w-4 h-4" />}
            disabled={!dirty || saving}
            isLoading={saving}
            onClick={handleSave}
          >
            Guardar
          </Button>
        </div>
      </Card>

      {/* Reconciliación de organización (solo en modo PERSON con org configurada) */}
      {config?.guestRegistrationMode === 'PERSON' && config.residentOrgIndexCode && (
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <ArrowRight className="w-4 h-4 text-amber-500" />
            <h3 className="text-base font-semibold text-theme">Mover residentes a nueva organización</h3>
          </div>
          <p className="text-sm text-theme-secondary mb-4">
            Mueve todos los residentes ya sincronizados en HikCentral a la organización{' '}
            <code className="font-mono text-xs bg-theme-bg-secondary px-1 rounded">{config.residentOrgIndexCode}</code>.
            El proceso es asíncrono y corre en batches de 50.
          </p>

          {reconcilePhase === 'running' && (
            <ReconcileProgress
              queued={reconcileQueued}
              progress={reconcileProgress}
            />
          )}

          {reconcilePhase === 'completed' && reconcileProgress && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm mb-3">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>
                {reconcileProgress.completed}/{reconcileProgress.total} residentes movidos
                {reconcileProgress.failed > 0 && ` · ${reconcileProgress.failed} fallaron`}
              </span>
            </div>
          )}

          {reconcilePhase === 'failed' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm mb-3">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>La reconciliación falló. Revisar logs del servidor y reintentar.</span>
            </div>
          )}

          {reconcilePhase !== 'running' && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RefreshCcw className="w-4 h-4" />}
              isLoading={reconcileStarting}
              disabled={reconcileStarting}
              onClick={handleReconcile}
            >
              {reconcilePhase === 'idle' ? 'Iniciar reconciliación' : 'Reintentar'}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface OrgSelectProps {
  label: string;
  value: string | null;
  orgs: HikCentralOrganization[];
  loading: boolean;
  onChange: (value: string | null) => void;
}

function OrgSelect({ label, value, orgs, loading, onChange }: OrgSelectProps) {
  return (
    <div>
      <label className="block text-xs font-medium text-theme-secondary mb-1">{label}</label>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-theme-secondary">
          <Loader2 className="w-3 h-3 animate-spin" />
          Cargando organizaciones…
        </div>
      ) : (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full border border-theme-border rounded-lg px-3 py-2 text-sm bg-theme-bg text-theme focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">— Seleccionar organización —</option>
          {orgs.map((o) => (
            <option key={o.orgIndexCode} value={o.orgIndexCode}>
              {o.orgName} ({o.orgIndexCode})
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

interface ReconcileProgressProps {
  queued: number | null;
  progress: { completed: number; total: number; failed: number } | null;
}

function ReconcileProgress({ queued, progress }: ReconcileProgressProps) {
  const pct = progress && progress.total > 0
    ? Math.round((progress.completed / progress.total) * 100)
    : 0;

  return (
    <div className="mb-4 space-y-2">
      <div className="flex items-center justify-between text-xs text-theme-secondary">
        <span className="flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {progress
            ? `${progress.completed} / ${progress.total} residentes`
            : queued !== null
            ? `${queued} batch${queued !== 1 ? 'es' : ''} encolado${queued !== 1 ? 's' : ''}…`
            : 'Iniciando…'}
        </span>
        {progress && <span>{pct}%</span>}
      </div>
      <div className="h-2 rounded-full bg-theme-bg-secondary overflow-hidden">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {progress?.failed !== undefined && progress.failed > 0 && (
        <p className="text-xs text-amber-600">{progress.failed} fallaron — se pueden reintentar via Full Sync.</p>
      )}
    </div>
  );
}
